import React, {FunctionComponent, useState} from 'react';
import {makeStyles} from 'tss-react/mui';
import {
    AppBar,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    InputLabel,
    Theme,
    Toolbar,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {FormProvider, useForm} from "react-hook-form";

import {useDisplayMessage} from "@/Utils/CustomHooks/useDisplayMessage";
import InputSelect from "@/Components/FormComponents/InputSelect/InputSelect";
import InputText from "@/Components/FormComponents/InputText/InputText";
import {
    CreateOrUpdateTranslationInput
} from "@/Domains/TenantManagement/Mutations/__generated__/CreateOrUpdateTranslationMutation.graphql";
import CreateOrUpdateTranslationMutation
    from "@/Domains/TenantManagement/Mutations/CreateOrUpdateTranslationMutation";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

interface EditComponentTranslationPopup {
    isOpen: string;
    setIsOpen: any;
    node: any;
    text: string;
    lang: string;
    setEdges: any;
    edges: any;
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },

    submit: {
        margin: theme.spacing(3, 0, 2),
    },

    buttonProgress: {
        position: "absolute",
        top: "54%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },

    wrapper: {
        margin: theme.spacing(1),
        marginTop: theme.spacing(2),
        position: "relative",
    },

    appBar: {
        position: "relative",
        boxShadow: "none",
    },

    title: {
        flex: 1,
    },

    formControl: {
        width: "100%",
    },
}));

const EditComponentTranslationPopup: FunctionComponent<EditComponentTranslationPopup> = ({
                                                                                             isOpen,
                                                                                             setIsOpen,
                                                                                             node,
                                                                                             text,
                                                                                             lang,
                                                                                             setEdges,
                                                                                             edges
                                                                                         }): JSX.Element => {

    const {classes} = useStyles();
    const {t} = useTranslation();
    const {displayMessage} = useDisplayMessage();

    const [tmpLanguage, setTmpLanguage] = useState(
        lang === "" || lang === null ? "de" : lang.toLowerCase(),
    );
    const [tmpNode, setTmpNode] = useState(node);
    const [tmpTranslationText, setTmpTranslationText] = useState(text);
    const [isLoading, setIsLoading] = useState(false);
    const [shouldReloadText, setShouldReloadText] = useState(true);

    const languages = [
        'de',
        'us',
        'es',
        'nl'
    ]

    const languageItems = languages.map((name: string, index: number) => ({
        id: name,
        name: name
    }));

    const defaultValues = {
        lang: lang === '' || lang === null ? 'de' : lang.toLowerCase(), // str,
        node: node, // str,
        text: text == "Error 369" ? "" : text // str,
    };

    const methods = useForm({
        mode: "onSubmit",
        defaultValues
    })

    // @ts-ignore
    const saveTranslation = (data, callback) => {
        setIsLoading(true);

        const input: CreateOrUpdateTranslationInput = {
            component: data.node,
            text: data.text,
            language: data.lang ? data.lang.toUpperCase() : tmpLanguage.toUpperCase()
        }

        CreateOrUpdateTranslationMutation(input, (err: any, result: any) => {
            if (err) {
                console.error(err.message);
                displayMessage(
                    "error",
                    t("translations.messages." + err.message),
                );
            }
            setIsLoading(false);
            setIsOpen("")
        });
    };

    // @ts-ignore
    const onSubmit = (data, shouldSavedBeforeAction: boolean, shouldWarnBeforeClose: boolean, callback: () => void) => {
        return new Promise((resolve, reject) => {
            saveTranslation(data, () => {
                callback();
                resolve(true);
            });

            return;
        })
    };

    return (
        <FormProvider {...methods}>
            <AppBar
                className={classes.appBar}
            >
                <Toolbar>
                    <Typography
                        variant="h6"
                        className={classes.title}
                    >
                        {!node ? t('translations.headline.addNewTranslation') : t('translations.headline.editTranslation')}
                    </Typography>
                    <IconButton aria-label="close"
                                onClick={() => {
                                    setIsOpen("");
                                }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>

            <DialogActions style={{
                justifyContent: "flex-start",
            }}>
                <>
                    <Button variant={"outlined"}
                            color={"primary"}
                            size={"small"}
                            startIcon={isLoading ? <CircularProgress size={22}/> : <CheckIcon/>}
                            disabled={!methods.getValues('text') || !methods.getValues('node') || !methods.getValues('lang') || isLoading}
                            onClick={methods.handleSubmit((data) => onSubmit(data, false, false, () => {
                                setIsOpen("");
                            }))}
                    >
                        {t('general.save')}
                    </Button>
                </>
            </DialogActions>
            <DialogContent>
                <Grid container
                      spacing={1}>
                    <Grid item
                          xs={12}>
                        <InputSelect label={t('translations.fields.language')}
                                     name={'lang'}
                                     items={languageItems}
                                     onChangeOverwrite={(event: any) => {
                                         if (node) {
                                             setEdges({
                                                 lang: event.target.value,
                                                 component: edges.component
                                             })
                                         }
                                     }}/>
                    </Grid>
                    <Grid item
                          xs={12}>
                        {node && <InputLabel>{t('translations.fields.component')}: {node}</InputLabel>}
                        {!node && <InputText label={t('translations.fields.component')}
                                             name={'node'}/>}
                    </Grid>
                    <Grid item
                          xs={12}>
                        <InputText label={t('translations.fields.translation')}
                                   name={'text'}
                                   rows={3}/>
                    </Grid>
                </Grid>
            </DialogContent>
        </FormProvider>
    );
};

export default EditComponentTranslationPopup;
